import React from 'react';
import parse from 'html-react-parser';
import { useIntl } from 'react-intl';
import { FlexColumn, FlexRow, FormField, Typography } from 'design-system';
import SurveyQuestionInput from '../SurveyQuestionInput';
import SurveyQuestionFieldError from '../SurveyQuestionFieldError';
import { normalizeId } from '../surveyPreviewUtils';

function questionUsesFormFieldWrapper(questionType, questionTypes) {
  return [questionTypes.TEXT, questionTypes.NUMBER, questionTypes.INVENTORY].includes(questionType);
}

function questionUsesInputErrorState(questionType, questionTypes) {
  return [questionTypes.TEXT, questionTypes.NUMBER, questionTypes.DROPDOWN, questionTypes.INVENTORY].includes(
    questionType
  );
}

function buildMultiSelectLimitHandlers({ question, intl, onSetQuestionError }) {
  if (!onSetQuestionError) {
    return {};
  }

  return {
    onMinError: () => {
      onSetQuestionError(question._id, {
        message: intl.formatMessage(
          { id: 'select-atleast-min-options' },
          { minOptions: question.min_options },
        ),
      });
    },
    onMaxError: () => {
      onSetQuestionError(question._id, {
        message: intl.formatMessage(
          { id: 'select-max-options' },
          { maxOptions: question.max_options },
        ),
      });
    },
    onClearErrors: () => {
      onSetQuestionError(question._id, null);
    },
  };
}

function renderSurveyQuestionField({
  question,
  questionTypes,
  survey,
  recipient,
  preview,
  surveyErrors,
  onSetQuestionError,
  onFinishSurvey,
  intl,
}) {
  const errorMessage =
    surveyErrors[normalizeId(question._id)]?.message ?? surveyErrors[question._id]?.message;
  const hasError = Boolean(errorMessage);
  const usesFormField = questionUsesFormFieldWrapper(question.questionType, questionTypes);
  const usesInputError = questionUsesInputErrorState(question.questionType, questionTypes);

  const input = (
    <SurveyQuestionInput
      question={question}
      questionTypes={questionTypes}
      survey={survey}
      recipient={recipient}
      preview={preview}
      hasError={usesInputError && hasError}
      onFinishSurvey={onFinishSurvey}
      {...buildMultiSelectLimitHandlers({ question, intl, onSetQuestionError })}
    />
  );

  if (usesFormField) {
    return (
      <FormField errorText={errorMessage} className="cc-preview-survey-question__field">
        {input}
      </FormField>
    );
  }

  return (
    <FlexColumn align="stretch" fullWidth className="cc-preview-survey-question__field">
      {input}
      <SurveyQuestionFieldError message={errorMessage} />
    </FlexColumn>
  );
}

function QuestionRequiredMarker() {
  return (
    <Typography component="span" className="cc-preview-survey-question__required" aria-hidden="true">
      *
    </Typography>
  );
}

const SurveySingleCard = ({
  questions,
  questionTypes,
  survey,
  recipient,
  step,
  surveyErrors = {},
  onSetQuestionError,
  onFinishSurvey,
  hideSurveyHeader = false,
}) => {
  const intl = useIntl();
  const visibleQuestions = (questions || []).filter(
    (question) => question.questionType !== questionTypes.EMPLOYEES && question.questionType !== 'skip'
  );
  const hasDirectorMessage = visibleQuestions.some((que) => que.questionType === 'director_message');
  const hideHeader = hideSurveyHeader || (hasDirectorMessage && step === 2) || visibleQuestions.length === 0;

  if (!visibleQuestions.length) {
    return null;
  }

  if (hideSurveyHeader) {
    return (
      <FlexColumn align="stretch" fullWidth className="cc-preview-survey-questions">
        {visibleQuestions.map((question, index) => {
          const questionNumber = question.displayIndex || index + 1;

          return (
            <FlexColumn
              align="stretch"
              key={question._id || `q-${questionNumber}`}
              className="cc-preview-survey-question"
            >
              {question.questionType !== 'director_message' ? (
                <FlexColumn align="start" fullWidth className="cc-preview-survey-question__copy">
                  <FlexRow align="start" fullWidth={false} className="cc-preview-survey-question__heading">
                    <Typography component="span" className="cc-preview-survey-question__number">
                      {questionNumber}.
                    </Typography>
                    <FlexColumn align="start" fullWidth className="cc-preview-survey-question__titles">
                      <Typography component="div" className="cc-preview-survey-question__text">
                        {parse(question?.text)}
                        {question.required ? <QuestionRequiredMarker /> : null}
                      </Typography>
                    </FlexColumn>
                  </FlexRow>
                </FlexColumn>
              ) : null}

              <FlexColumn align="stretch" fullWidth className="cc-preview-survey-question__input">
                {renderSurveyQuestionField({
                  question,
                  questionTypes,
                  survey,
                  recipient,
                  preview: true,
                  surveyErrors,
                  onSetQuestionError,
                  onFinishSurvey,
                  intl,
                })}
              </FlexColumn>
            </FlexColumn>
          );
        })}
      </FlexColumn>
    );
  }

  return (
    <FlexColumn align="stretch" className="question-card">
      {!hideHeader ? (
        <>
          <Typography
            variant="h3"
            fontWeight="bold"
            className="text-primary text-center pb-1"
            style={{ fontSize: '1.75rem' }}
          >
            {intl.formatMessage({ id: 'please-give-us-your-feedback' })}
          </Typography>

          <Typography component="div" variant="body1" className="text-center pb-4 mb-2" style={{ fontSize: '18px' }}>
            {parse(survey.description)}
          </Typography>
        </>
      ) : null}

      <FlexRow wrap align="start" justify="start" fullWidth>
        {visibleQuestions.map((question) => (
          <FlexColumn
            align="stretch"
            key={question._id || `q-${question.displayIndex}`}
            className={`px-4 ${hideHeader ? 'pb-3' : 'py-3'} single-view-question-card ${
              question.questionType === 'director_message' ? 'w-100' : ''
            }`}
          >
            {question.questionType !== 'director_message' ? (
              <FlexColumn align="center" fullWidth className="question-title">
                <Typography variant="body1" fontWeight="bold" style={{ fontSize: '1.25rem' }}>
                  {question.category}
                </Typography>

                <Typography component="div" variant="body1" className="mt-2">
                  {parse(question?.text)}
                  {question.required ? <QuestionRequiredMarker /> : null}
                </Typography>
              </FlexColumn>
            ) : null}

            <FlexColumn align="stretch" fullWidth>
              {renderSurveyQuestionField({
                question,
                questionTypes,
                survey,
                recipient,
                preview: false,
                surveyErrors,
                onSetQuestionError,
                onFinishSurvey,
                intl,
              })}
            </FlexColumn>
          </FlexColumn>
        ))}
      </FlexRow>
    </FlexColumn>
  );
};

export default SurveySingleCard;
